import Papa from 'papaparse'
import type { ParseResult } from 'papaparse'
import { createElement, Fragment } from 'react'
import type { ChangeEvent, Dispatch, ReactNode } from 'react'
import { addMessage } from '../../../../Flux/Slice/Application/ApplicationSlice.js'
import { errorMessage } from '../../../../Model/Message/IMessage.js'
import type { ConfirmFn } from '../../../Component/ConfirmProvider.js'
import type { UnknownAction } from '@reduxjs/toolkit'
import { detectEncoding, friendlyEncodingName } from './detectEncoding.js'

export type PapaHeuristicsResult = {
  options?: PapaConfigOption
  errors: string[]
}

export type PapaConfigOption = {
  description: ReactNode
  config: Papa.ParseLocalConfig<unknown, File>
  requiresConfirm: boolean
}

/** Render one or more notice strings as separate paragraphs for the confirm dialog. */
function renderMessage(parts: string[]): ReactNode {
  return createElement(
    Fragment,
    null,
    ...parts.map((text, i) => createElement('p', { key: i, style: i > 0 ? { marginTop: '0.75rem' } : undefined }, text))
  )
}

export async function handleCSVUpload(
  event: ChangeEvent<HTMLInputElement>,
  confirm: ConfirmFn,
  dispatch: Dispatch<UnknownAction>,
  complete: (data: ParseResult<unknown>) => void
) {
  const files = event.target.files
  if (!files || files.length === 0) return

  const config = await determinePapaConfig(files[0], (data) => {
    complete(data)
    event.target.value = ''
  })
  if (config.errors.length > 0 || !config.options) {
    dispatch(addMessage(errorMessage(`Could not handle csv: ${config.errors.join(', ')}`)))
    return
  }

  const doParse = () => {
    if (!config.options?.config) {
      throw Error('Papa broken')
    }
    Papa.parse(files[0], config.options?.config)
  }

  if (config.options.requiresConfirm) {
    await confirm(
      {
        title: 'Confirm CSV Settings',
        message: config.options.description
      },
      doParse
    )
  } else {
    doParse()
  }
}

/**
 * Attempt to infer the most likely parse config by looking at the first two lines of the file
 *
 * @param file
 * @param complete
 */
export async function determinePapaConfig(file: File, complete: (data: ParseResult<unknown>) => void): Promise<PapaHeuristicsResult> {
  const detected = await detectEncoding(file)
  // When the encoding is only a guess, surface it and force a confirm — nobody
  // (human or detector) can be certain from the bytes of a short file alone.
  const encodingNote = detected.confident
    ? ''
    : `We couldn't reliably determine this file's text encoding and will read it as ${friendlyEncodingName(detected.encoding)}. If accented characters (ä, ö, ü, ß, …) look wrong afterwards, re-save the file as "CSV UTF-8" and upload again.`
  const withEncoding = (config: Papa.ParseLocalConfig<unknown, File>): Papa.ParseLocalConfig<unknown, File> => ({
    ...config,
    // Forwarded to FileReader.readAsText(file, encoding) so Papa reads the raw
    // File with the detected charset instead of the browser's UTF-8 default.
    encoding: detected.encoding
  })
  const text = detected.text
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '')
  const sampleLines = lines.slice(0, 2)
  if (sampleLines.length < 2) {
    return {
      options: undefined,
      errors: ['CSV seems to short, at least two lines should be contained']
    }
  }

  const delimiters = [',', ';', '\t']
  const delimiterNames: Record<string, string> = {
    ',': 'Comma',
    ';': 'Semicolon',
    '\t': 'Tab'
  }

  // Count delimiter occurrences across sampled lines
  const counts = delimiters.map((d) => {
    /* "what if I split by this delimiter" */
    const split = sampleLines.map((line) => line.split(d))
    const all = split.flatMap((x) => x)
    return {
      delimiter: d,
      /* number of this delimiter */
      totalCount: split[0].length - 1 + split[1].length - 1,
      /* if the number of delimiters across sampled lines is the same */
      consistent: split.length === 2 ? split[0].length === split[1].length : true,
      contained: delimiters.filter((x) => x !== d).filter((x) => all.some((l) => l.includes(x)))
    }
  })

  const nonZero = counts.filter((c) => c.totalCount > 0)

  if (nonZero.length > 0) {
    const res = nonZero.sort((a, b) => b.totalCount - a.totalCount)[0]
    let delimiterMessage: string = ''
    if (!res.consistent && res.contained.length > 0) {
      delimiterMessage = `The CSV delimiter has been determined as ${delimiterNames[res.delimiter]}, but column counts seem inconsistent and other delimiters have been found, too (${res.contained.map((x) => delimiterNames[x]).join(', ')}). Confirm if this is the intended CSV separator or abort and fix your file.`
    } else if (!res.consistent) {
      delimiterMessage = `The CSV delimiter has been determined as ${delimiterNames[res.delimiter]}, but column counts seem inconsistent. Confirm if this is the intended CSV separator or abort and fix your file.`
    } else if (res.contained.length > 0) {
      delimiterMessage = `The CSV delimiter has been determined as ${delimiterNames[res.delimiter]}, but other delimiters have been found, too (${res.contained.map((x) => delimiterNames[x]).join(', ')}). Confirm if this is the intended CSV separator or abort and fix your file.`
    }
    const parts = [encodingNote, delimiterMessage].filter((m) => m !== '')
    return {
      errors: [],
      options: {
        description: renderMessage(parts),
        config: withEncoding({
          delimiter: res.delimiter,
          skipEmptyLines: true,
          header: true,
          complete: complete
        }),
        requiresConfirm: parts.length > 0
      }
    }
  }

  const parts = [encodingNote, 'No strong delimiter signal detected. Falling back to comma.'].filter((m) => m !== '')
  return {
    errors: [],
    options: {
      description: renderMessage(parts),
      config: withEncoding({
        delimiter: ',',
        skipEmptyLines: true,
        header: true,
        complete: complete
      }),
      // The delimiter fallback is informational only; a confirm is warranted just for the encoding guess.
      requiresConfirm: encodingNote !== ''
    }
  }
}
